"use client";

import { useEffect, useState } from "react";

const CHANNEL_NAME = "innova-nexus-presence";
const STORAGE_KEY = "innova-nexus-presence-tabs";
const HEARTBEAT_MS = 15_000;
const STALE_AFTER_MS = HEARTBEAT_MS * 3;
const DISCOVERY_MS = 180;

type PresenceRecord = Record<string, number>;

export function useNexusFreshEntry() {
  const [isFreshEntry, setIsFreshEntry] = useState<boolean | null>(null);

  useEffect(() => {
    const tabId = createTabId();
    let disposed = false;
    let existingTabDetected = false;
    const channel =
      typeof BroadcastChannel === "undefined"
        ? null
        : new BroadcastChannel(CHANNEL_NAME);

    function announcePresence() {
      channel?.postMessage({ tabId, type: "PRESENT" });
      writePresence(tabId);
    }

    function handleMessage(event: MessageEvent<unknown>) {
      if (!isPresenceMessage(event.data) || event.data.tabId === tabId) return;
      if (event.data.type === "PROBE") {
        announcePresence();
      }
      if (event.data.type === "PRESENT") {
        existingTabDetected = true;
      }
    }

    function handleStorage(event: StorageEvent) {
      if (event.key !== STORAGE_KEY) return;
      const tabs = parsePresence(event.newValue);
      existingTabDetected = Object.entries(tabs).some(
        ([id, seenAt]) => id !== tabId && Date.now() - seenAt < STALE_AFTER_MS,
      );
    }

    const existingTabs = readPresence();
    existingTabDetected = Object.entries(existingTabs).some(
      ([id, seenAt]) => id !== tabId && Date.now() - seenAt < STALE_AFTER_MS,
    );
    channel?.addEventListener("message", handleMessage);
    window.addEventListener("storage", handleStorage);
    channel?.postMessage({ tabId, type: "PROBE" });
    announcePresence();

    const discoveryTimer = window.setTimeout(() => {
      if (!disposed) setIsFreshEntry(!existingTabDetected);
    }, DISCOVERY_MS);
    const heartbeat = window.setInterval(announcePresence, HEARTBEAT_MS);

    function cleanup() {
      removePresence(tabId);
      channel?.postMessage({ tabId, type: "BYE" });
    }

    window.addEventListener("pagehide", cleanup);
    return () => {
      disposed = true;
      window.clearTimeout(discoveryTimer);
      window.clearInterval(heartbeat);
      window.removeEventListener("pagehide", cleanup);
      window.removeEventListener("storage", handleStorage);
      channel?.removeEventListener("message", handleMessage);
      cleanup();
      channel?.close();
    };
  }, []);

  return isFreshEntry;
}

function createTabId() {
  return window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

function isPresenceMessage(
  value: unknown,
): value is { tabId: string; type: "PROBE" | "PRESENT" | "BYE" } {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { tabId?: unknown }).tabId === "string" &&
    ["PROBE", "PRESENT", "BYE"].includes(
      (value as { type?: string }).type ?? "",
    )
  );
}

function readPresence() {
  return prunePresence(parsePresence(window.localStorage.getItem(STORAGE_KEY)));
}

function writePresence(tabId: string) {
  const next = readPresence();
  next[tabId] = Date.now();
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function removePresence(tabId: string) {
  const next = readPresence();
  delete next[tabId];
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function parsePresence(value: string | null): PresenceRecord {
  if (!value) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] => typeof entry[1] === "number",
      ),
    );
  } catch {
    return {};
  }
}

function prunePresence(tabs: PresenceRecord) {
  const now = Date.now();
  return Object.fromEntries(
    Object.entries(tabs).filter(([, seenAt]) => now - seenAt < STALE_AFTER_MS),
  );
}
